import { useState, useEffect, useCallback } from 'react';
import {
  CalendarMetrics,
  ConnectedCalendar,
  Appointment,
  CalendarFilters,
  Reminder,
} from '../types';
import { calendarIntegrationService } from '../services/calendarIntegration';
import { Calendar } from '../types';
import { handleApiCatch } from '@utils/apiErrorHandler';

export const useCalendars = () => {
  const [metrics, setMetrics] = useState<CalendarMetrics | null>(null);
  const [connectedCalendars, setConnectedCalendars] = useState<ConnectedCalendar[]>([]);

  const [appointments, setAppointments] = useState<Appointment[]>([
    {
      id: '1',
      date: 'Dec 15, 2024',
      time: 'Today, 2:00 PM',
      client: {
        name: 'John Smith',
        company: 'Acme Corporation',
        avatar: 'AC',
      },
      type: 'sales',
      agent: 'Sarah Johnson',
      duration: 30,
      status: 'confirmed',
    },
    {
      id: '2',
      date: 'Dec 16, 2024',
      time: 'Tomorrow, 10:00 AM',
      client: {
        name: 'Mike Wilson',
        company: 'TechCorp Solutions',
        avatar: 'TC',
      },
      type: 'support',
      agent: 'Michael Brown',
      duration: 45,
      status: 'confirmed',
    },
    {
      id: '3',
      date: 'Dec 17, 2024',
      time: 'Dec 17, 3:30 PM',
      client: {
        name: 'Lisa Davis',
        company: 'Global Consulting',
        avatar: 'GC',
      },
      type: 'demo',
      agent: 'John Smith',
      duration: 60,
      status: 'pending',
    },
  ]);

  const [reminders] = useState<Reminder[]>([
    {
      id: '1',
      appointment: 'Acme Corporation - Sales Call',
      reminderTime: '1 hour before',
      method: 'email',
      status: 'sent',
    },
    {
      id: '2',
      appointment: 'TechCorp Solutions - Support Call',
      reminderTime: '30 minutes before',
      method: 'sms',
      status: 'pending',
    },
  ]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CalendarFilters>({
    search: '',
    type: '',
    status: '',
  });

  const fetchMetrics = useCallback(async () => {
    try {
      const stats = await calendarIntegrationService.getStats();

      // Transform CalendarStats to CalendarMetrics format
      const transformedMetrics: CalendarMetrics = {
        total_appointments: stats.total_events,
        upcoming: stats.upcoming_events,
        completed: stats.past_events,
        cancelled: stats.failed_events,
      };

      setMetrics(transformedMetrics);
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch calendar metrics');
      setError(processedErrors.generalError || 'Failed to fetch calendar metrics');
    }
  }, []);

  const fetchCalendars = useCallback(async () => {
    try {
      const calendars = await calendarIntegrationService.list();

      // Transform Calendar[] to ConnectedCalendar[] format
      const transformedCalendars: ConnectedCalendar[] = calendars.map((calendar: Calendar) => ({
        id: calendar.id,
        name: calendar.name || calendar.provider_display, // Use name if present, otherwise provider_display
        organization: calendar.organization_name,
        provider: calendar.provider,
        providerDisplay: calendar.provider_display,
        providerCalendarId: calendar.email, // Use email as calendar ID
        status: calendar.status,
        statusDisplay: calendar.status_display,
        isActive: calendar.is_active,
        isConnected: calendar.status === 'active',
        settings: {}, // No settings in new API
        appointmentCount: calendar.event_count,
        errorMessage: calendar.error_message || undefined,
        timeslots: calendar.timeslots,
        business_hours:
          calendar.business_hours &&
          calendar.business_hours !== '{}' &&
          calendar.business_hours !== 'null'
            ? calendar.business_hours
            : undefined,
        created: calendar.created,
        modified: calendar.modified,
      }));
      setConnectedCalendars(transformedCalendars);
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch calendars');
      setError(processedErrors.generalError || 'Failed to fetch calendars');
    }
  }, []);

  const fetchCalendarsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchMetrics(), fetchCalendars()]);
    } catch (err) {
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [fetchMetrics, fetchCalendars]);

  const connectCalendar = async (
    calendarData: Omit<ConnectedCalendar, 'id' | 'appointmentCount' | 'created' | 'modified'>
  ) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const newCalendar: ConnectedCalendar = {
        ...calendarData,
        id: Date.now().toString(),
        appointmentCount: 0,
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
      };
      setConnectedCalendars(prev => [...prev, newCalendar]);
    } catch (err) {
      throw new Error('Failed to connect calendar');
    }
  };

  const disconnectCalendar = async (id: string) => {
    try {
      await calendarIntegrationService.delete(id);
      setConnectedCalendars(prev => prev.filter(calendar => calendar.id !== id));
    } catch (err) {
      throw new Error('Failed to disconnect calendar');
    }
  };

  const scheduleAppointment = async (appointmentData: Omit<Appointment, 'id'>) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const newAppointment: Appointment = {
        ...appointmentData,
        id: Date.now().toString(),
      };
      setAppointments(prev => [...prev, newAppointment]);
    } catch (err) {
      throw new Error('Failed to schedule appointment');
    }
  };

  const cancelAppointment = async (id: string) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      setAppointments(prev => prev.filter(appointment => appointment.id !== id));
    } catch (err) {
      throw new Error('Failed to cancel appointment');
    }
  };

  useEffect(() => {
    fetchCalendarsData();
  }, [fetchCalendarsData]);

  return {
    metrics,
    connectedCalendars,
    appointments,
    reminders,
    loading,
    error,
    filters,
    setFilters,
    connectCalendar,
    disconnectCalendar,
    scheduleAppointment,
    cancelAppointment,
    refetch: fetchCalendarsData,
  };
};
